/**
 * The committed trivia-board artifact (#295) — the SEO/play content that ships in
 * the build. Grown offline by `trivia:generate` (built on `@jeopardy/generation`)
 * and owner-reviewed, mirroring the `corpus.v1.json` precedent.
 *
 * DO NOT hand-edit the `boards` payload — regenerate it with `trivia:generate` so
 * every clue passes the shared generation quality + answer-leak gates. Metadata
 * flags (`published`) may be toggled by hand during review.
 */

import type { TriviaBoardSet } from './types';

export const TRIVIA_BOARD_SET: TriviaBoardSet = {
  version: '396337b0',
  generatedAtEpochMs: 1788491521930,
  boards: [
    {
      boardId: 'sixth-grade',
      theme: 'Sixth Grade',
      description:
        'A themed sixth-grade trivia board — history, science, literature, geography, math, and more. Perfect for a family game night or a classroom warm-up.',
      audience: 'Family',
      published: true,
      pack: {
        name: 'Sixth Grade — Peril',
        version: '1.0.0',
        rounds: [
          {
            round: 'JEOPARDY',
            categories: [
              {
                name: 'Science',
                clues: [
                  {
                    id: 'j-0-0',
                    prompt:
                      'This closest star to Earth provides the light and heat that make life on our planet possible.',
                    response: 'What is the Sun?',
                    value: 200,
                  },
                  {
                    id: 'j-0-1',
                    prompt:
                      'This process in green plants converts sunlight, water, and carbon dioxide into glucose and oxygen.',
                    response: 'What is photosynthesis?',
                    value: 400,
                  },
                  {
                    id: 'j-0-2',
                    prompt:
                      'With the atomic number 8, this element makes up about 21% of the air we breathe and is essential for respiration.',
                    response: 'What is oxygen?',
                    value: 600,
                  },
                  {
                    id: 'j-0-3',
                    prompt:
                      'This subatomic particle carries a negative charge and orbits the nucleus of an atom.',
                    response: 'What is an electron?',
                    value: 800,
                  },
                  {
                    id: 'j-0-4',
                    prompt:
                      "This scientist's general theory of relativity, published in 1915, describes gravity as the curving of space and time.",
                    response: 'Who is Albert Einstein?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'History',
                clues: [
                  {
                    id: 'j-1-0',
                    prompt:
                      'This ship struck an iceberg on its maiden voyage in 1912 and sank in the North Atlantic.',
                    response: 'What is the Titanic?',
                    value: 200,
                  },
                  {
                    id: 'j-1-1',
                    prompt:
                      'This wall dividing a German city fell in 1989, symbolizing the end of the Cold War era.',
                    response: 'What is the Berlin Wall?',
                    value: 400,
                  },
                  {
                    id: 'j-1-2',
                    prompt:
                      'This Egyptian queen, the last active ruler of the Ptolemaic Kingdom, allied with both Julius Caesar and Mark Antony.',
                    response: 'Who is Cleopatra?',
                    value: 600,
                  },
                  {
                    id: 'j-1-3',
                    prompt:
                      'Signed in 1215 by King John of England, this document limited royal power and influenced later ideas of liberty.',
                    response: 'What is the Magna Carta?',
                    value: 800,
                  },
                  {
                    id: 'j-1-4',
                    prompt:
                      'This 1789 event, marked by the storming of the Bastille, overthrew the monarchy and led to the rise of Napoleon.',
                    response: 'What is the French Revolution?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Geography',
                clues: [
                  {
                    id: 'j-2-0',
                    prompt:
                      'This longest river in the world flows northward through Egypt before emptying into the Mediterranean Sea.',
                    response: 'What is the Nile?',
                    value: 200,
                  },
                  {
                    id: 'j-2-1',
                    prompt:
                      "Home to over 1.4 billion people, this Asian nation has the world's largest population and hosts the Great Wall.",
                    response: 'What is China?',
                    value: 400,
                  },
                  {
                    id: 'j-2-2',
                    prompt:
                      'This South American mountain range, the longest on Earth, stretches along the western edge of the continent for about 4,300 miles.',
                    response: 'What are the Andes?',
                    value: 600,
                  },
                  {
                    id: 'j-2-3',
                    prompt:
                      "This landlocked African body of water, one of the world's largest lakes, borders Uganda, Kenya, and Tanzania and shares its name with a British queen.",
                    response: 'What is Lake Victoria?',
                    value: 800,
                  },
                  {
                    id: 'j-2-4',
                    prompt:
                      'This narrow waterway separating Europe from Asia in Turkey connects the Black Sea to the Sea of Marmara.',
                    response: 'What is the Bosporus?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Animals',
                clues: [
                  {
                    id: 'j-3-0',
                    prompt:
                      "This 'king of the jungle' actually lives on the African savanna, where the males are known for their impressive manes.",
                    response: 'What is a lion?',
                    value: 200,
                  },
                  {
                    id: 'j-3-1',
                    prompt:
                      'The tallest land animal on Earth, this creature uses its long neck to munch leaves from the tops of acacia trees.',
                    response: 'What is a giraffe?',
                    value: 400,
                  },
                  {
                    id: 'j-3-2',
                    prompt:
                      'This aquatic mammal, the largest animal to have ever lived, communicates through low-frequency songs across ocean distances.',
                    response: 'What is a blue whale?',
                    value: 600,
                  },
                  {
                    id: 'j-3-3',
                    prompt:
                      'Native to Australia, this egg-laying mammal has a duck-like bill and webbed feet, and the male has a venomous spur.',
                    response: 'What is a platypus?',
                    value: 800,
                  },
                  {
                    id: 'j-3-4',
                    prompt:
                      'This spotted African cat is the fastest land animal, reaching speeds of about 70 miles per hour in short bursts.',
                    response: 'What is a cheetah?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Sports',
                clues: [
                  {
                    id: 'j-4-0',
                    prompt:
                      'In this game, players dribble a ball and try to score by shooting it through a hoop 10 feet off the ground.',
                    response: 'What is basketball?',
                    value: 200,
                  },
                  {
                    id: 'j-4-1',
                    prompt:
                      "This sport, sometimes called 'the beautiful game,' is the most popular in the world, with players using their feet to move the ball into a net.",
                    response: 'What is soccer?',
                    value: 400,
                  },
                  {
                    id: 'j-4-2',
                    prompt:
                      'This four-year event, held in both summer and winter versions, features athletes competing under a flag with five interlocking rings.',
                    response: 'What are the Olympics?',
                    value: 600,
                  },
                  {
                    id: 'j-4-3',
                    prompt:
                      'In this racket sport, players compete on grass, clay, or hard courts at events like Wimbledon and the U.S. Open.',
                    response: 'What is tennis?',
                    value: 800,
                  },
                  {
                    id: 'j-4-4',
                    prompt:
                      'This individual sport combines cross-country skiing with rifle marksmanship and is a Winter Games staple.',
                    response: 'What is the biathlon?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Space',
                clues: [
                  {
                    id: 'j-5-0',
                    prompt:
                      'This reddish neighbor, nicknamed for its rusty appearance, is the target of NASA rovers like Perseverance and Curiosity.',
                    response: 'What is Mars?',
                    value: 200,
                  },
                  {
                    id: 'j-5-1',
                    prompt:
                      'The largest planet in our solar system, this gas giant sports a swirling storm called the Great Red Spot.',
                    response: 'What is Jupiter?',
                    value: 400,
                  },
                  {
                    id: 'j-5-2',
                    prompt:
                      'This dwarf planet, demoted from full planetary status in 2006, has a heart-shaped region named Tombaugh Regio.',
                    response: 'What is Pluto?',
                    value: 600,
                  },
                  {
                    id: 'j-5-3',
                    prompt:
                      'This cloud of gas and dust in space, such as the Orion one, can serve as a stellar nursery where new stars are born.',
                    response: 'What is a nebula?',
                    value: 800,
                  },
                  {
                    id: 'j-5-4',
                    prompt:
                      'This region beyond the eighth planet is a disk of icy bodies and the source of many short-period comets.',
                    response: 'What is the Kuiper Belt?',
                    value: 1000,
                  },
                ],
              },
            ],
          },
          {
            round: 'DOUBLE_JEOPARDY',
            categories: [
              {
                name: 'Literature',
                clues: [
                  {
                    id: 'dj-0-0',
                    prompt:
                      "This English playwright of the late 1500s and early 1600s wrote 'Romeo and Juliet' and 'Hamlet' and is often called the Bard of Avon.",
                    response: 'Who is William Shakespeare?',
                    value: 400,
                  },
                  {
                    id: 'dj-0-1',
                    prompt:
                      "In a novel by George Orwell, the citizens of Oceania are constantly reminded that this all-seeing figure 'is watching you.'",
                    response: 'Who is Big Brother?',
                    value: 800,
                  },
                  {
                    id: 'dj-0-2',
                    prompt:
                      'This American author created the mischievous boys Tom Sawyer and Huckleberry Finn.',
                    response: 'Who is Mark Twain?',
                    value: 1200,
                  },
                  {
                    id: 'dj-0-3',
                    prompt:
                      "This literary term for giving a story's ending away is what we ask people to avoid when discussing a plot twist.",
                    response: 'What is a spoiler?',
                    value: 1600,
                  },
                  {
                    id: 'dj-0-4',
                    prompt:
                      "This Greek epic attributed to Homer follows a hero's ten-year journey home after the Trojan War.",
                    response: 'What is the Odyssey?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Math',
                clues: [
                  {
                    id: 'dj-1-0',
                    prompt:
                      "This number, symbolized by the Greek letter pi, equals a circle's circumference divided by its diameter and is roughly 3.14159.",
                    response: 'What is pi?',
                    value: 400,
                  },
                  {
                    id: 'dj-1-1',
                    prompt:
                      'In a right triangle, this theorem states that the square of the longest side equals the sum of the squares of the other two sides.',
                    response: 'What is the Pythagorean theorem?',
                    value: 800,
                  },
                  {
                    id: 'dj-1-2',
                    prompt:
                      "This branch of math, whose name means 'measuring the earth,' deals with points, lines, angles, and shapes like triangles and circles.",
                    response: 'What is geometry?',
                    value: 1200,
                    isDailyDouble: true,
                  },
                  {
                    id: 'dj-1-3',
                    prompt:
                      'This irrational constant, approximately 2.71828 and named for a Swiss mathematician born in 1707, is the base of the natural logarithm.',
                    response: "What is e (Euler's number)?",
                    value: 1600,
                  },
                  {
                    id: 'dj-1-4',
                    prompt:
                      'This positive value of a number, written with two vertical bars around it, represents its distance from zero on the number line.',
                    response: 'What is absolute value?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'World Capitals',
                clues: [
                  {
                    id: 'dj-2-0',
                    prompt:
                      'This French city on the Seine River is famous for a certain iron tower built in 1889.',
                    response: 'What is Paris?',
                    value: 400,
                  },
                  {
                    id: 'dj-2-1',
                    prompt:
                      'This Asian city, home to the Imperial Palace and the busy Shibuya Crossing, hosted the 2020 Summer Olympics.',
                    response: 'What is Tokyo?',
                    value: 800,
                  },
                  {
                    id: 'dj-2-2',
                    prompt:
                      'Located on the Nile River, this African capital sits near the ancient pyramids of Giza.',
                    response: 'What is Cairo?',
                    value: 1200,
                  },
                  {
                    id: 'dj-2-3',
                    prompt:
                      'This South American capital, one of the highest in the world, was founded by Spanish conquistador Francisco Pizarro in 1535.',
                    response: 'What is Lima?',
                    value: 1600,
                  },
                  {
                    id: 'dj-2-4',
                    prompt:
                      'This Scandinavian capital, spread across 14 islands, is home to the ABBA Museum and the Vasa warship.',
                    response: 'What is Stockholm?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Art',
                clues: [
                  {
                    id: 'dj-3-0',
                    prompt:
                      "This Dutch painter with a fiery temper famously cut off part of his own ear and painted swirling night skies like 'The Starry Night'.",
                    response: 'Who is Vincent van Gogh?',
                    value: 400,
                  },
                  {
                    id: 'dj-3-1',
                    prompt:
                      "This Italian Renaissance genius painted the 'Mona Lisa' and 'The Last Supper' while also filling notebooks with inventions.",
                    response: 'Who is Leonardo da Vinci?',
                    value: 800,
                  },
                  {
                    id: 'dj-3-2',
                    prompt:
                      'This form of art uses tiny colored pieces of glass, stone, or tile pressed together to create a larger image.',
                    response: 'What is a mosaic?',
                    value: 1200,
                  },
                  {
                    id: 'dj-3-3',
                    prompt:
                      "This Spanish co-founder of Cubism painted the massive anti-war mural 'Guernica'.",
                    response: 'Who is Pablo Picasso?',
                    value: 1600,
                  },
                  {
                    id: 'dj-3-4',
                    prompt:
                      'This 19th-century French movement, named after a Monet painting of a sunrise, focused on capturing light and momentary visual impressions with loose brushstrokes.',
                    response: 'What is Impressionism?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Music',
                clues: [
                  {
                    id: 'dj-4-0',
                    prompt:
                      'This handheld percussion instrument has jingling metal discs around its frame and is often shaken or struck against the palm.',
                    response: 'What is a tambourine?',
                    value: 400,
                  },
                  {
                    id: 'dj-4-1',
                    prompt:
                      "This term describes how loud or soft music is played, with 'forte' meaning loud and 'piano' meaning soft.",
                    response: 'What are dynamics?',
                    value: 800,
                  },
                  {
                    id: 'dj-4-2',
                    prompt:
                      'This group of five horizontal lines and four spaces is where musical notes are written to show their pitch.',
                    response: 'What is a staff (or stave)?',
                    value: 1200,
                  },
                  {
                    id: 'dj-4-3',
                    prompt:
                      "This Italian word for the speed of a piece is measured in beats per minute, with 'allegro' being fast and 'adagio' being slow.",
                    response: 'What is tempo?',
                    value: 1600,
                    isDailyDouble: true,
                  },
                  {
                    id: 'dj-4-4',
                    prompt:
                      'This symbol placed at the beginning of a staff, such as the treble or bass type, determines the pitch of the written notes.',
                    response: 'What is a clef?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Technology',
                clues: [
                  {
                    id: 'dj-5-0',
                    prompt:
                      'This handheld communication device fits in your pocket, lets you text, browse the web, and run downloadable apps.',
                    response: 'What is a smartphone?',
                    value: 400,
                  },
                  {
                    id: 'dj-5-1',
                    prompt:
                      'This global network of interconnected computers lets you send emails and stream videos across the world.',
                    response: 'What is the Internet?',
                    value: 800,
                  },
                  {
                    id: 'dj-5-2',
                    prompt:
                      'This branch of computer science aims to make machines think and learn like humans, powering chatbots and self-driving cars.',
                    response: 'What is artificial intelligence?',
                    value: 1200,
                  },
                  {
                    id: 'dj-5-3',
                    prompt:
                      "This distributed digital ledger, famous for powering cryptocurrencies, records transactions across many computers so they can't be easily altered.",
                    response: 'What is blockchain?',
                    value: 1600,
                  },
                  {
                    id: 'dj-5-4',
                    prompt:
                      'This wireless communication standard, whose name is symbolized by two coins, lets your earbuds connect to your phone over short distances.',
                    response: 'What is Bluetooth?',
                    value: 2000,
                  },
                ],
              },
            ],
          },
        ],
        finalJeopardy: {
          categoryName: 'World Geography',
          prompt:
            'This landlocked South American nation, one of only two on the continent without a coastline, has two capital cities: one administrative and one that serves as the seat of its judiciary.',
          response: 'What is Bolivia?',
        },
        defaultTimerDuration: 20,
        maxPlayers: 8,
      },
    },
    {
      boardId: '80s-movies',
      theme: '80s Movies',
      description:
        'Twelve categories of 80s movie trivia — blockbusters, cult classics, comedies, action heroes, and the iconic soundtracks. Play the full board with friends.',
      audience: 'Adults',
      published: true,
      pack: {
        name: '80s Movies — Peril',
        version: '1.0.0',
        rounds: [
          {
            round: 'JEOPARDY',
            categories: [
              {
                name: 'Blockbusters',
                clues: [
                  {
                    id: 'j-0-0',
                    prompt:
                      'This 1975 Steven Spielberg thriller about a great white shark terrorizing a beach town is often credited as the first summer blockbuster.',
                    response: 'What is Jaws?',
                    value: 200,
                  },
                  {
                    id: 'j-0-1',
                    prompt:
                      "James Cameron's 1997 epic romance set aboard a doomed ocean liner won 11 Academy Awards and long held the box-office record.",
                    response: 'What is Titanic?',
                    value: 400,
                  },
                  {
                    id: 'j-0-2',
                    prompt:
                      'In this 1993 Spielberg film, cloned dinosaurs run amok at a theme park on Isla Nublar.',
                    response: 'What is Jurassic Park?',
                    value: 600,
                  },
                  {
                    id: 'j-0-3',
                    prompt:
                      "This 2009 Cameron sci-fi film about the moon Pandora and the blue-skinned Na'vi became the highest-grossing movie of its time.",
                    response: 'What is Avatar?',
                    value: 800,
                  },
                  {
                    id: 'j-0-4',
                    prompt:
                      "This 2019 Marvel film culminating the Infinity Saga overtook Cameron's Pandora epic as the top-grossing movie ever.",
                    response: 'What is Avengers: Endgame?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Comedies',
                clues: [
                  {
                    id: 'j-1-0',
                    prompt:
                      'In this 1993 Bill Murray film, a weatherman relives the same February holiday over and over in Punxsutawney.',
                    response: 'What is Groundhog Day?',
                    value: 200,
                  },
                  {
                    id: 'j-1-1',
                    prompt:
                      'This 2004 film follows a 1970s San Diego TV news team led by anchorman Ron Burgundy.',
                    response: 'What is Anchorman?',
                    value: 400,
                  },
                  {
                    id: 'j-1-2',
                    prompt:
                      "In this 1980 spoof, a passenger must land a jetliner after the crew falls ill; 'Surely you can't be serious.'",
                    response: 'What is Airplane!?',
                    value: 600,
                  },
                  {
                    id: 'j-1-3',
                    prompt:
                      "This 1998 Coen brothers film centers on a laid-back Los Angeles slacker known as 'the Dude' and his love of bowling.",
                    response: 'What is The Big Lebowski?',
                    value: 800,
                  },
                  {
                    id: 'j-1-4',
                    prompt:
                      "This 1975 Monty Python film sends King Arthur on a quest, encountering killer rabbits and knights who say 'Ni!'",
                    response: 'What is Monty Python and the Holy Grail?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Action Heroes',
                clues: [
                  {
                    id: 'j-2-0',
                    prompt:
                      "This muscular Austrian-accented star promised he'd 'be back' as a time-traveling cyborg in a 1984 James Cameron film.",
                    response: 'Who is Arnold Schwarzenegger?',
                    value: 200,
                  },
                  {
                    id: 'j-2-1',
                    prompt:
                      "This barefoot New York cop crawled through air ducts of Nakatomi Plaza on Christmas Eve, yelling 'Yippee-ki-yay!'",
                    response: 'Who is John McClane?',
                    value: 400,
                  },
                  {
                    id: 'j-2-2',
                    prompt:
                      'This bull-whip-cracking, fedora-wearing archaeologist searched for the Ark of the Covenant and the Holy Grail.',
                    response: 'Who is Indiana Jones?',
                    value: 600,
                  },
                  {
                    id: 'j-2-3',
                    prompt:
                      "This Colonial Marine survivor repeatedly battled acid-blooded xenomorphs, memorably declaring 'Get away from her, you...!'",
                    response: 'Who is Ellen Ripley?',
                    value: 800,
                  },
                  {
                    id: 'j-2-4',
                    prompt:
                      'This licensed-to-kill British agent, code 007, has been portrayed by Sean Connery, Roger Moore, and Daniel Craig.',
                    response: 'Who is James Bond?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Sci-Fi & Fantasy',
                clues: [
                  {
                    id: 'j-3-0',
                    prompt:
                      "This author created a boy wizard named Harry who attends a school of witchcraft and wizardry after learning he's famous in the magical world.",
                    response: 'Who is J.K. Rowling?',
                    value: 200,
                  },
                  {
                    id: 'j-3-1',
                    prompt:
                      'In this George Lucas franchise, a farm boy discovers he can wield an energy field known as the Force to battle a galactic empire.',
                    response: 'What is Star Wars?',
                    value: 400,
                  },
                  {
                    id: 'j-3-2',
                    prompt:
                      'J.R.R. Tolkien set his tales of hobbits, elves, and a certain gold band of power in this fictional realm.',
                    response: 'What is Middle-earth?',
                    value: 600,
                  },
                  {
                    id: 'j-3-3',
                    prompt:
                      "This English author's 1932 novel imagines a future society whose citizens are bred in bottles and pacified by a drug called soma.",
                    response: 'Who is Aldous Huxley?',
                    value: 800,
                  },
                  {
                    id: 'j-3-4',
                    prompt:
                      "Frank Herbert's 1965 epic follows Paul Atreides on the desert planet Arrakis, sole source of the spice melange.",
                    response: 'What is Dune?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Romances',
                clues: [
                  {
                    id: 'j-4-0',
                    prompt:
                      'In this 1990 film, a murdered man communicates with his grieving girlfriend through a psychic played by Whoopi Goldberg during a famous pottery scene.',
                    response: 'What is Ghost?',
                    value: 200,
                  },
                  {
                    id: 'j-4-1',
                    prompt:
                      'Set in the fictional town of Seattle, this 1993 Nora Ephron film features a widower whose son calls a radio show, ultimately reuniting him with a woman atop the Empire State Building.',
                    response: 'What is Sleepless in Seattle?',
                    value: 400,
                  },
                  {
                    id: 'j-4-2',
                    prompt:
                      "Julia Roberts plays a woman who walks into a London travel bookshop owned by Hugh Grant's character in this 1999 film named for a London neighborhood.",
                    response: 'What is Notting Hill?',
                    value: 600,
                  },
                  {
                    id: 'j-4-3',
                    prompt:
                      'Based on a Nicholas Sparks novel, this 2004 film has an elderly man reading a story to a woman with dementia about two young lovers named Noah and Allie.',
                    response: 'What is The Notebook?',
                    value: 800,
                  },
                  {
                    id: 'j-4-4',
                    prompt:
                      "In this 1942 classic set in Morocco during WWII, Humphrey Bogart's saloon owner reunites with a former flame played by Ingrid Bergman.",
                    response: 'What is Casablanca?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Cult Classics',
                clues: [
                  {
                    id: 'j-5-0',
                    prompt:
                      'This 1975 musical romp, still shown at midnight screenings where fans throw rice and shout at the screen, features Tim Curry as a sweet transvestite named Frank-N-Furter.',
                    response: 'What is The Rocky Horror Picture Show?',
                    value: 200,
                  },
                  {
                    id: 'j-5-1',
                    prompt:
                      "Tyler Durden invites Edward Norton to join an underground group with a first rule you're not supposed to talk about in this 1999 David Fincher film.",
                    response: 'What is Fight Club?',
                    value: 400,
                  },
                  {
                    id: 'j-5-2',
                    prompt:
                      'In this 1998 stoner comedy, two friends embark on an epic quest across New Jersey to reach a fast-food chain, joined by silent Bob and his chatty companion.',
                    response: 'What is Half Baked?',
                    value: 600,
                  },
                  {
                    id: 'j-5-3',
                    prompt:
                      'Time travel, a giant menacing rabbit named Frank, and a jet engine crashing into a suburban home fuel this 2001 Richard Kelly film starring Jake Gyllenhaal.',
                    response: 'What is Donnie Darko?',
                    value: 800,
                  },
                  {
                    id: 'j-5-4',
                    prompt:
                      "This 1984 sci-fi comedy about a rock band with amps that go to eleven pioneered the 'mockumentary' style and gave us the tune 'Big Bottom'.",
                    response: 'What is This Is Spinal Tap?',
                    value: 1000,
                  },
                ],
              },
            ],
          },
          {
            round: 'DOUBLE_JEOPARDY',
            categories: [
              {
                name: 'Directors',
                clues: [
                  {
                    id: 'dj-0-0',
                    prompt:
                      'This bearded maker of "Jaws" and "E.T." co-founded the DreamWorks studio in 1994.',
                    response: 'Who is Steven Spielberg?',
                    value: 400,
                  },
                  {
                    id: 'dj-0-1',
                    prompt:
                      'Known for the \'Vertigo effect\' and cameo appearances in his own films, this Englishman helmed "Psycho" and "The Birds."',
                    response: 'Who is Alfred Hitchcock?',
                    value: 800,
                  },
                  {
                    id: 'dj-0-2',
                    prompt:
                      "This Japanese filmmaker's 1954 epic about seven warriors defending a village was later remade as an American Western.",
                    response: 'Who is Akira Kurosawa?',
                    value: 1200,
                  },
                  {
                    id: 'dj-0-3',
                    prompt:
                      'This New Zealander won a Best Director Oscar for the final chapter of a fantasy trilogy filmed in his home country.',
                    response: 'Who is Peter Jackson?',
                    value: 1600,
                  },
                  {
                    id: 'dj-0-4',
                    prompt:
                      'This first African American to win Best Director captured the award for the 2021 film "Nomadland."',
                    response: 'Who is Chloé Zhao?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Sequels',
                clues: [
                  {
                    id: 'dj-1-0',
                    prompt:
                      "This 1991 follow-up to a hit dinosaur-cloning film... wait, actually this 1991 comedy sequel found Kevin's family accidentally leaving him behind again, this time stranded in New York City.",
                    response: 'What is Home Alone 2: Lost in New York?',
                    value: 400,
                  },
                  {
                    id: 'dj-1-1',
                    prompt:
                      'Released in 2003, this Pixar sequel followed a clownfish father searching the ocean... no; instead, this second installment of a toy-filled franchise sees Woody kidnapped by a greedy collector named Al.',
                    response: 'What is Toy Story 2?',
                    value: 800,
                  },
                  {
                    id: 'dj-1-2',
                    prompt:
                      "In this 1974 crime-drama follow-up, Robert De Niro plays the younger version of a Sicilian immigrant who rises to power, in a tale told alongside his son Michael's reign.",
                    response: 'What is The Godfather Part II?',
                    value: 1200,
                    isDailyDouble: true,
                  },
                  {
                    id: 'dj-1-3',
                    prompt:
                      'This 2004 animated sequel sends a green ogre and his bride to meet her royal in-laws in a kingdom Far Far Away.',
                    response: 'What is Shrek 2?',
                    value: 1600,
                  },
                  {
                    id: 'dj-1-4',
                    prompt:
                      'This 1989 third-outing sequel teams an adventurous archaeologist with his bookish father, played by Sean Connery, on a quest for the Holy Grail.',
                    response: 'What is Indiana Jones and the Last Crusade?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Soundtracks',
                clues: [
                  {
                    id: 'dj-2-0',
                    prompt:
                      'Vangelis won an Oscar for his synth-driven score to this 1981 film about British sprinters at the 1924 Olympics.',
                    response: 'What is Chariots of Fire?',
                    value: 400,
                  },
                  {
                    id: 'dj-2-1',
                    prompt:
                      "Bill Conti composed 'Gonna Fly Now' for this 1976 boxing drama starring Sylvester Stallone.",
                    response: 'What is Rocky?',
                    value: 800,
                  },
                  {
                    id: 'dj-2-2',
                    prompt:
                      'The Bee Gees powered the disco-drenched soundtrack of this 1977 John Travolta dance film.',
                    response: 'What is Saturday Night Fever?',
                    value: 1200,
                  },
                  {
                    id: 'dj-2-3',
                    prompt:
                      'Prince wrote and performed the songs for this 1984 semi-autobiographical film set in Minneapolis.',
                    response: 'What is Purple Rain?',
                    value: 1600,
                  },
                  {
                    id: 'dj-2-4',
                    prompt:
                      "Ennio Morricone's whistling, twangy theme accompanied Clint Eastwood in this 1966 Sergio Leone spaghetti Western.",
                    response: 'What is The Good, the Bad and the Ugly?',
                    value: 2000,
                  },
                ],
              },
              {
                name: '80s Villains',
                clues: [
                  {
                    id: 'dj-3-0',
                    prompt:
                      "This heavy-breathing, black-caped enforcer of the Galactic Empire revealed a shocking family secret to Luke Skywalker in 1980's 'The Empire Strikes Back'.",
                    response: 'Who is Darth Vader?',
                    value: 400,
                  },
                  {
                    id: 'dj-3-1',
                    prompt:
                      "In 1988's 'Die Hard', Alan Rickman played this suave German thief who seized the Nakatomi tower with a crew of heavily armed henchmen.",
                    response: 'Who is Hans Gruber?',
                    value: 800,
                  },
                  {
                    id: 'dj-3-2',
                    prompt:
                      "This tall, grinning boxer from the Soviet Union declared 'I must break you' before facing Rocky Balboa in the 1985 fourth installment of the franchise.",
                    response: 'Who is Ivan Drago?',
                    value: 1200,
                  },
                  {
                    id: 'dj-3-3',
                    prompt:
                      'This dream-invading, burn-scarred killer with a razor-fingered glove first terrorized Elm Street teenagers in 1984.',
                    response: 'Who is Freddy Krueger?',
                    value: 1600,
                  },
                  {
                    id: 'dj-3-4',
                    prompt:
                      "This power-mad head of the Weyland-Yutani corporation, played by Paul Reiser in 1986's 'Aliens', schemed to smuggle a xenomorph specimen back to Earth.",
                    response: 'Who is Carter Burke?',
                    value: 2000,
                  },
                ],
              },
              {
                name: '80s Sidekicks',
                clues: [
                  {
                    id: 'dj-4-0',
                    prompt:
                      'This Falkor-like luckdragon carried Atreyu through the skies in a 1984 film about a boy reading a magical book.',
                    response: 'What is Falkor?',
                    value: 400,
                  },
                  {
                    id: 'dj-4-1',
                    prompt:
                      "In a 1984 blockbuster, this proton-pack-toting green ghost with an insatiable appetite haunted a hotel before joining the team's mascot lore.",
                    response: 'What is Slimer?',
                    value: 800,
                  },
                  {
                    id: 'dj-4-2',
                    prompt:
                      'This wisecracking robot voiced by Tim Blaney rode along with a lovable machine named Number 5 in a 1986 comedy.',
                    response: 'Who is... (there is no distinct sidekick here)',
                    value: 1200,
                  },
                  {
                    id: 'dj-4-3',
                    prompt:
                      'This robotic canine could transform into a spring, a jet, and a submarine to help a blue Capcom hero battle Dr. Wily starting in 1987.',
                    response: 'Who is Rush?',
                    value: 1600,
                    isDailyDouble: true,
                  },
                  {
                    id: 'dj-4-4',
                    prompt:
                      'This mogwai turned gremlin leader in a 1984 horror-comedy tormented a small town after some rules were broken.',
                    response: 'Who is Stripe?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Franchise Reboots',
                clues: [
                  {
                    id: 'dj-5-0',
                    prompt:
                      'This 2009 J.J. Abrams film relaunched a sci-fi saga with a young Kirk and Spock, using time travel to create an alternate timeline for the Enterprise crew.',
                    response: 'What is Star Trek?',
                    value: 400,
                  },
                  {
                    id: 'dj-5-1',
                    prompt:
                      'Christopher Nolan reinvigorated the Caped Crusader in 2005 with this darker origin film introducing Christian Bale as the Gotham hero.',
                    response: 'What is Batman Begins?',
                    value: 800,
                  },
                  {
                    id: 'dj-5-2',
                    prompt:
                      'In 2015 this dinosaur-park thriller revived a dormant Spielberg-created series, opening a fully operational island attraction on Isla Nublar.',
                    response: 'What is Jurassic World?',
                    value: 1200,
                  },
                  {
                    id: 'dj-5-3',
                    prompt:
                      'This 2018 movie erased sequels since 1978, reuniting Jamie Lee Curtis with masked killer Michael Myers under the same name as the original slasher.',
                    response: 'What is Halloween?',
                    value: 1600,
                  },
                  {
                    id: 'dj-5-4',
                    prompt:
                      'This 2010 martial-arts remake starring Jaden Smith and Jackie Chan actually featured kung fu, despite its title referencing a Japanese discipline.',
                    response: 'What is The Karate Kid?',
                    value: 2000,
                  },
                ],
              },
            ],
          },
        ],
        finalJeopardy: {
          categoryName: 'Quotable Lines',
          prompt:
            'This detective character, created by Arthur Conan Doyle and residing at 221B Baker Street, is often associated with a deductive catchphrase he never actually uttered in the original stories.',
          response: 'Who is Sherlock Holmes?',
        },
        defaultTimerDuration: 20,
        maxPlayers: 8,
      },
    },
    {
      boardId: 'christmas',
      theme: 'Christmas',
      description:
        'Twelve categories of Christmas trivia — movies, songs, traditions, foods, characters, and more. A ready-to-play holiday game board.',
      audience: 'Family',
      published: true,
      pack: {
        name: 'Christmas — Peril',
        version: '1.0.0',
        rounds: [
          {
            round: 'JEOPARDY',
            categories: [
              {
                name: 'Christmas Movies',
                clues: [
                  {
                    id: 'j-0-0',
                    prompt:
                      'In this 1990 comedy, 8-year-old Kevin McCallister is left behind when his family jets off to Paris for the holidays.',
                    response: 'What is Home Alone?',
                    value: 200,
                  },
                  {
                    id: 'j-0-1',
                    prompt:
                      'Ralphie dreams of getting a Red Ryder BB gun in this 1983 film narrated by a grown-up looking back on his childhood.',
                    response: 'What is A Christmas Story?',
                    value: 400,
                  },
                  {
                    id: 'j-0-2',
                    prompt:
                      "Buddy, raised by Santa's helpers at the North Pole, journeys to New York City to find his real dad in this 2003 Will Ferrell comedy.",
                    response: 'What is Elf?',
                    value: 600,
                  },
                  {
                    id: 'j-0-3',
                    prompt:
                      'Jack Skellington, king of a spooky holiday town, discovers a festive winter celebration in this 1993 Tim Burton stop-motion musical.',
                    response: 'What is The Nightmare Before Christmas?',
                    value: 800,
                  },
                  {
                    id: 'j-0-4',
                    prompt:
                      'John McClane battles terrorists in the Nakatomi Plaza high-rise in this 1988 action film often debated as a holiday classic.',
                    response: 'What is Die Hard?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Christmas Songs',
                clues: [
                  {
                    id: 'j-1-0',
                    prompt:
                      "In this classic, an animal with a glowing facial feature is mocked by his peers until fog rolls in and he guides Santa's transport that night.",
                    response: 'What is Rudolph the Red-Nosed Reindeer?',
                    value: 200,
                  },
                  {
                    id: 'j-1-1',
                    prompt:
                      'This tune counts off a dozen days of increasingly extravagant gifts, starting with a bird in a pear tree.',
                    response: 'What is The Twelve Days of Christmas?',
                    value: 400,
                  },
                  {
                    id: 'j-1-2',
                    prompt:
                      'Written by Irving Berlin and made famous by Bing Crosby, this song dreams of a snowy holiday just like the ones the singer used to know.',
                    response: 'What is White Christmas?',
                    value: 600,
                  },
                  {
                    id: 'j-1-3',
                    prompt:
                      "In this cheerful sleigh-riding song, the singer laughs 'ha-ha-ha' while dashing through the snow in a one-horse open vehicle.",
                    response: 'What is Jingle Bells?',
                    value: 800,
                  },
                  {
                    id: 'j-1-4',
                    prompt:
                      "This 1994 Mariah Carey hit declares the singer doesn't want presents under the tree—there's just one person she desires for the holiday.",
                    response: 'What is All I Want for Christmas Is You?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Santa & Elves',
                clues: [
                  {
                    id: 'j-2-0',
                    prompt:
                      "This jolly gift-giver in a red suit is said to keep a list of who's been naughty or nice.",
                    response: 'Who is Santa Claus?',
                    value: 200,
                  },
                  {
                    id: 'j-2-1',
                    prompt:
                      'Santa is said to travel the world in a single night riding in this vehicle pulled through the sky by his team.',
                    response: 'What is a sleigh?',
                    value: 400,
                  },
                  {
                    id: 'j-2-2',
                    prompt:
                      'At his workshop, these pointy-eared helpers build and wrap the toys before the big delivery.',
                    response: 'What are elves?',
                    value: 600,
                  },
                  {
                    id: 'j-2-3',
                    prompt:
                      'This chilly, gift-wrapping season is when Santa makes his famous journey down chimneys around the globe.',
                    response: 'What is Christmas?',
                    value: 800,
                  },
                  {
                    id: 'j-2-4',
                    prompt:
                      "Santa's toy-making headquarters is traditionally said to be located at this frozen top-of-the-world spot.",
                    response: 'What is the North Pole?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Holiday TV Specials',
                clues: [
                  {
                    id: 'j-3-0',
                    prompt:
                      'In this 1965 animated classic, this round-headed boy picks out a scrawny little tree for the school play.',
                    response: 'What is A Charlie Brown Christmas?',
                    value: 200,
                  },
                  {
                    id: 'j-3-1',
                    prompt:
                      "This 1966 tale features a green, grumpy creature living on Mount Crumpit who tries to steal the Whos' festive celebration.",
                    response: 'What is How the Grinch Stole Christmas!?',
                    value: 400,
                  },
                  {
                    id: 'j-3-2',
                    prompt:
                      "This 1964 stop-motion special introduced a jolly gift-giver's origin story, narrated by a mailman voiced by Fred Astaire.",
                    response: "What is Santa Claus Is Comin' to Town?",
                    value: 600,
                  },
                  {
                    id: 'j-3-3',
                    prompt:
                      'In this 1966 special that first aired on Halloween, Linus waits in a patch all night for a flying gourd-bringer who never shows.',
                    response: "What is It's the Great Pumpkin, Charlie Brown?",
                    value: 800,
                  },
                  {
                    id: 'j-3-4',
                    prompt:
                      "This 1974 stop-motion film follows a heat-averse snowman and his miser brother, telling the story of a certain jolly figure's youth.",
                    response: 'What is The Year Without a Santa Claus?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Christmas Characters',
                clues: [
                  {
                    id: 'j-4-0',
                    prompt:
                      "This jolly, red-suited gift-giver keeps a list of who's been naughty or nice and travels the world in one night.",
                    response: 'Who is Santa Claus?',
                    value: 200,
                  },
                  {
                    id: 'j-4-1',
                    prompt:
                      'With his shiny, glowing nose, this ninth reindeer guides the sleigh through foggy Christmas Eve skies.',
                    response: 'Who is Rudolph?',
                    value: 400,
                  },
                  {
                    id: 'j-4-2',
                    prompt:
                      "This green, cave-dwelling grump with a heart 'two sizes too small' tries to steal the holiday from Whoville.",
                    response: 'Who is the Grinch?',
                    value: 600,
                  },
                  {
                    id: 'j-4-3',
                    prompt:
                      'In a Dickens tale, this bitter miser is visited by three spirits who show him his past, present, and future.',
                    response: 'Who is Ebenezer Scrooge?',
                    value: 800,
                  },
                  {
                    id: 'j-4-4',
                    prompt:
                      "This talking snowman in a Disney film longs to experience summer and famously loves 'warm hugs.'",
                    response: 'Who is Olaf?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Winter',
                clues: [
                  {
                    id: 'j-5-0',
                    prompt:
                      'These six-sided ice crystals fall from clouds and, according to popular belief, no two are exactly alike.',
                    response: 'What are snowflakes?',
                    value: 200,
                  },
                  {
                    id: 'j-5-1',
                    prompt:
                      'On this holiday that falls on December 25th, many families decorate an evergreen tree and exchange gifts.',
                    response: 'What is Christmas?',
                    value: 400,
                  },
                  {
                    id: 'j-5-2',
                    prompt:
                      'Athletes race down icy tracks or snowy slopes at this international sporting event held every four years, with a version dedicated to snow and ice sports.',
                    response: 'What are the Winter Olympics?',
                    value: 600,
                  },
                  {
                    id: 'j-5-3',
                    prompt:
                      'This shortest day of the year, occurring around December 21st in the Northern Hemisphere, marks the astronomical start of the coldest season.',
                    response: 'What is the winter solstice?',
                    value: 800,
                  },
                  {
                    id: 'j-5-4',
                    prompt:
                      'This eight-day Jewish festival of lights, celebrated in late fall or early winter, involves lighting a menorah over successive nights.',
                    response: 'What is Hanukkah?',
                    value: 1000,
                  },
                ],
              },
            ],
          },
          {
            round: 'DOUBLE_JEOPARDY',
            categories: [
              {
                name: 'Christmas Around the World',
                clues: [
                  {
                    id: 'dj-0-0',
                    prompt:
                      'In this most populous South American nation, holiday celebrations happen during summer, so beachgoers might spot a jolly bearded gift-giver in lighter clothing.',
                    response: 'What is Brazil?',
                    value: 400,
                  },
                  {
                    id: 'dj-0-1',
                    prompt:
                      "In this Scandinavian country, families celebrate 'Julaften' on December 24th, and legend says they hide their brooms so witches can't steal them for a midnight ride.",
                    response: 'What is Norway?',
                    value: 800,
                  },
                  {
                    id: 'dj-0-2',
                    prompt:
                      'In this Central European nation, children polish their boots on December 5th so that St. Nicholas can fill them with treats or, for the naughty, a lump of coal.',
                    response: 'What is Germany?',
                    value: 1200,
                  },
                  {
                    id: 'dj-0-3',
                    prompt:
                      'In this East Asian country, where few residents are Christian, a fried chicken fast-food meal has become a wildly popular holiday tradition thanks to a 1970s ad campaign.',
                    response: 'What is Japan?',
                    value: 1600,
                  },
                  {
                    id: 'dj-0-4',
                    prompt:
                      'In this nation home to the Colosseum, an old woman named La Befana flies on a broomstick to deliver gifts to children on the eve of the Epiphany.',
                    response: 'What is Italy?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Christmas Traditions',
                clues: [
                  {
                    id: 'dj-1-0',
                    prompt:
                      "You hang this fabric footwear by the fireplace, hoping it'll be filled with treats instead of coal by morning.",
                    response: 'What is a stocking?',
                    value: 400,
                  },
                  {
                    id: 'dj-1-1',
                    prompt:
                      'Kissing is customary when two people stand beneath this white-berried plant hung in a doorway.',
                    response: 'What is mistletoe?',
                    value: 800,
                  },
                  {
                    id: 'dj-1-2',
                    prompt:
                      'This German-born evergreen custom involves decorating a cut conifer with lights and ornaments indoors.',
                    response: 'What is the Christmas tree?',
                    value: 1200,
                    isDailyDouble: true,
                  },
                  {
                    id: 'dj-1-3',
                    prompt:
                      "In this Mexican celebration held on nine nights before December 25th, participants reenact Mary and Joseph's search for lodging.",
                    response: 'What is Las Posadas?',
                    value: 1600,
                  },
                  {
                    id: 'dj-1-4',
                    prompt:
                      'This candlelit German countdown wreath or calendar marks the four Sundays leading up to the holiday.',
                    response: 'What is Advent?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Christmas Foods',
                clues: [
                  {
                    id: 'dj-2-0',
                    prompt:
                      'This spiral-shaped red-and-white peppermint candy is often hung on trees or used to stir hot cocoa during the holidays.',
                    response: 'What is a candy cane?',
                    value: 400,
                  },
                  {
                    id: 'dj-2-1',
                    prompt:
                      "Often called 'figgy pudding' in an old carol, this dense, steamed British dessert is packed with dried fruit and sometimes set aflame at the table.",
                    response: 'What is plum pudding?',
                    value: 800,
                  },
                  {
                    id: 'dj-2-2',
                    prompt:
                      'This spiced, cookie-like man decorated with icing buttons and a smiling face is baked into a house-building tradition.',
                    response: 'What is a gingerbread man?',
                    value: 1200,
                  },
                  {
                    id: 'dj-2-3',
                    prompt:
                      'This creamy, sweet drink flavored with nutmeg and made from milk and eggs is sometimes spiked with rum for grown-up parties.',
                    response: 'What is eggnog?',
                    value: 1600,
                  },
                  {
                    id: 'dj-2-4',
                    prompt:
                      'This dense Italian fruit-and-nut confection, dusted with powdered sugar and sometimes wrapped in edible paper, hails from the city of Siena.',
                    response: 'What is panforte?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Christmas Stories',
                clues: [
                  {
                    id: 'dj-3-0',
                    prompt:
                      "In Dickens' classic, this miserly businessman is visited by three spirits who show him the error of his cold-hearted ways.",
                    response: 'Who is Ebenezer Scrooge?',
                    value: 400,
                  },
                  {
                    id: 'dj-3-1',
                    prompt:
                      'In this Dr. Seuss tale, a green grump living on Mount Crumpit tries to steal the holiday from the cheerful folks of Whoville.',
                    response: 'What is How the Grinch Stole Christmas?',
                    value: 800,
                  },
                  {
                    id: 'dj-3-2',
                    prompt:
                      "This poem beginning ''Twas the night before...' introduced many popular details of the jolly gift-giver we know today.",
                    response: "What is 'A Visit from St. Nicholas'?",
                    value: 1200,
                  },
                  {
                    id: 'dj-3-3',
                    prompt:
                      "In O. Henry's touching short story, a young couple each secretly sells a treasured possession to buy the other a present.",
                    response: "What is 'The Gift of the Magi'?",
                    value: 1600,
                  },
                  {
                    id: 'dj-3-4',
                    prompt:
                      'In this ballet based on an E.T.A. Hoffmann story, a girl named Clara receives a toy soldier that battles the Mouse King.',
                    response: 'What is The Nutcracker?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Christmas Decorations',
                clues: [
                  {
                    id: 'dj-4-0',
                    prompt:
                      'This evergreen conifer, often a fir or spruce, is set up indoors and adorned with lights and ornaments.',
                    response: 'What is a Christmas tree?',
                    value: 400,
                  },
                  {
                    id: 'dj-4-1',
                    prompt:
                      'Hang this circular arrangement of evergreen boughs, often tied with a bow, on your front door for the holidays.',
                    response: 'What is a wreath?',
                    value: 800,
                  },
                  {
                    id: 'dj-4-2',
                    prompt:
                      'This white kissing-plant with waxy berries gets hung in a doorway; pause beneath it at your own risk.',
                    response: 'What is mistletoe?',
                    value: 1200,
                  },
                  {
                    id: 'dj-4-3',
                    prompt:
                      "These small twinkling bulbs on a wire strand, once called 'fairy' versions, brighten up trees and rooflines.",
                    response: 'What are string lights?',
                    value: 1600,
                    isDailyDouble: true,
                  },
                  {
                    id: 'dj-4-4',
                    prompt:
                      'This thin, shimmering tinsel-like garland, whose name comes from a German word for spun gold, drapes over branches.',
                    response: 'What is tinsel garland?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Christmas Gifts',
                clues: [
                  {
                    id: 'dj-5-0',
                    prompt:
                      'This plush toy stuffed with fluff, often shaped like a bear, is a classic soft present for younger siblings on Christmas morning.',
                    response: 'What is a teddy bear?',
                    value: 400,
                  },
                  {
                    id: 'dj-5-1',
                    prompt:
                      "Kids who've been extra good might unwrap this two-wheeled ride, which you pedal down the street once the snow melts.",
                    response: 'What is a bicycle?',
                    value: 800,
                  },
                  {
                    id: 'dj-5-2',
                    prompt:
                      "This handheld device for playing games on the go, made by companies like Nintendo, tops many teenagers' wish lists.",
                    response: 'What is a video game console?',
                    value: 1200,
                  },
                  {
                    id: 'dj-5-3',
                    prompt:
                      'Made of interlocking plastic bricks, this Danish building set lets you construct castles, spaceships, and cities.',
                    response: 'What is LEGO?',
                    value: 1600,
                  },
                  {
                    id: 'dj-5-4',
                    prompt:
                      'This slippery winter sport gift has two blades that let you glide across a frozen pond or an indoor rink.',
                    response: 'What are ice skates?',
                    value: 2000,
                  },
                ],
              },
            ],
          },
        ],
        finalJeopardy: {
          categoryName: 'Christmas Around the World',
          prompt:
            'In this Scandinavian country, families traditionally gather to watch a Disney holiday TV special at 3 PM on the 24th, and their gift-bearing gnome-like figure is called the "tomte."',
          response: 'What is Sweden?',
        },
        defaultTimerDuration: 20,
        maxPlayers: 8,
      },
    },
    {
      boardId: 'halloween',
      theme: 'Halloween',
      description:
        'A themed Halloween trivia board — horror movies, costumes, monsters, witches, and more spooky fun. Play the full board with friends.',
      audience: 'Family',
      published: true,
      pack: {
        name: 'Halloween — Peril',
        version: '1.0.1',
        rounds: [
          {
            round: 'JEOPARDY',
            categories: [
              {
                name: 'Horror Movies',
                clues: [
                  {
                    id: 'j-0-0',
                    prompt:
                      'This 1975 Steven Spielberg blockbuster about a beach town terrorized by a great white shark made audiences afraid to go in the water.',
                    response: 'What is Jaws?',
                    value: 200,
                  },
                  {
                    id: 'j-0-1',
                    prompt:
                      'In this 1984 Wes Craven film, teens are stalked in their dreams by a burned killer named Freddy Krueger, who wears a glove with blades.',
                    response: 'What is A Nightmare on Elm Street?',
                    value: 400,
                  },
                  {
                    id: 'j-0-2',
                    prompt:
                      'This 1996 slasher film features a killer in a white ghost-face mask who quizzes victims about scary-movie rules before attacking.',
                    response: 'What is Scream?',
                    value: 600,
                  },
                  {
                    id: 'j-0-3',
                    prompt:
                      'In this 2017 adaptation of a Stephen King novel, a group of kids called the Losers Club faces a shape-shifting entity that appears as a clown named Pennywise.',
                    response: 'What is It?',
                    value: 800,
                  },
                  {
                    id: 'j-0-4',
                    prompt:
                      'This 1980 Stanley Kubrick film follows a writer who becomes caretaker of the isolated Overlook Hotel and slowly loses his mind.',
                    response: 'What is The Shining?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Trick or Treat',
                clues: [
                  {
                    id: 'j-1-0',
                    prompt:
                      "This orange fruit is carved with a scary face and lit from within to make a glowing jack-o'-lantern.",
                    response: 'What is a pumpkin?',
                    value: 200,
                  },
                  {
                    id: 'j-1-1',
                    prompt:
                      'This bony figure of the human frame, stripped of flesh, dangles as a classic Halloween decoration and rattles through spooky cartoons.',
                    response: 'What is a skeleton?',
                    value: 400,
                  },
                  {
                    id: 'j-1-2',
                    prompt:
                      'This blood-drinking creature of the night fears garlic and sunlight and can supposedly be stopped with a wooden stake.',
                    response: 'What is a vampire?',
                    value: 600,
                  },
                  {
                    id: 'j-1-3',
                    prompt:
                      'On the full moon, a person cursed by a bite transforms into this howling half-human, half-canine beast.',
                    response: 'What is a werewolf?',
                    value: 800,
                  },
                  {
                    id: 'j-1-4',
                    prompt:
                      'This word from Yoruba and Haitian folklore describes a shambling, undead corpse with an appetite for the living.',
                    response: 'What is a zombie?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Ghosts & Ghouls',
                clues: [
                  {
                    id: 'j-2-0',
                    prompt:
                      'This eerie, flickering light seen hovering over bogs at night — blamed on marsh gas — was said in folklore to lure travelers off their path.',
                    response: "What is a will-o'-the-wisp?",
                    value: 200,
                  },
                  {
                    id: 'j-2-1',
                    prompt:
                      "According to Scottish and Irish folklore, this wailing female spirit's shrieking cry outside a home foretells an approaching death in the family.",
                    response: 'What is a banshee?',
                    value: 400,
                  },
                  {
                    id: 'j-2-2',
                    prompt:
                      "German for 'noisy ghost,' this troublesome spirit is blamed for slamming doors, flying objects, and mysterious knocking sounds in a house.",
                    response: 'What is a poltergeist?',
                    value: 600,
                  },
                  {
                    id: 'j-2-3',
                    prompt:
                      'In German folklore, this ghostly look-alike or double of a living person is regarded as an omen of misfortune or death.',
                    response: 'What is a doppelgänger?',
                    value: 800,
                  },
                  {
                    id: 'j-2-4',
                    prompt:
                      'In Mexican folklore, this weeping female ghost — her name meaning "the crying woman" — drifts along rivers searching for her lost children.',
                    response: 'What is La Llorona?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Witches & Warlocks',
                clues: [
                  {
                    id: 'j-3-0',
                    prompt:
                      'In "The Wizard of Oz," this green-skinned villainess of the West melts when Dorothy splashes her with water.',
                    response: 'Who is the Wicked Witch of the West?',
                    value: 200,
                  },
                  {
                    id: 'j-3-1',
                    prompt:
                      'In Shakespeare\'s "Macbeth," three of these spellcasters greet the title character with the prophecy that he\'ll be king.',
                    response: 'Who are the Three Witches (the Weird Sisters)?',
                    value: 400,
                  },
                  {
                    id: 'j-3-2',
                    prompt:
                      'Salem, this New England state, gave its name to the infamous 1692 trials of accused practitioners of the dark arts.',
                    response: 'What is Massachusetts?',
                    value: 600,
                  },
                  {
                    id: 'j-3-3',
                    prompt:
                      'This nose-less dark sorcerer, the greatest enemy of Harry Potter, was born Tom Riddle.',
                    response: 'Who is Lord Voldemort?',
                    value: 800,
                  },
                  {
                    id: 'j-3-4',
                    prompt:
                      'In Arthurian legend, this powerful enchantress and half-sister of King Arthur is often his sorcerous adversary.',
                    response: 'Who is Morgan le Fay?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Pumpkins & Lanterns',
                clues: [
                  {
                    id: 'j-4-0',
                    prompt:
                      'This large, round orange gourd that we carve at Halloween grows on a vine and is technically a fruit; its seeds can be roasted for a tasty snack.',
                    response: 'What is a squash?',
                    value: 200,
                  },
                  {
                    id: 'j-4-1',
                    prompt:
                      'Irish immigrants brought the tradition of carving lanterns to this North American country, where the big orange gourd became the go-to choice.',
                    response: 'What is the United States?',
                    value: 400,
                  },
                  {
                    id: 'j-4-2',
                    prompt:
                      'The Irish folk tale behind the carved lantern features a trickster with this common first name who is doomed to wander with a glowing ember.',
                    response: 'Who is Jack (Stingy Jack)?',
                    value: 600,
                  },
                  {
                    id: 'j-4-3',
                    prompt:
                      "This U.S. state, home to the town of Half Moon Bay's famous weigh-off, hosts one of the country's most celebrated giant-gourd growing competitions each October.",
                    response: 'What is California?',
                    value: 800,
                  },
                  {
                    id: 'j-4-4',
                    prompt:
                      "This Slavic holiday word, meaning 'All Saints' or the eve before it, is the ancient Celtic festival Samhain's Christian successor that inspired autumn lantern-carving customs.",
                    response: 'What is Hallowmas (Allhallows/Hallowtide)?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Halloween Candy',
                clues: [
                  {
                    id: 'j-5-0',
                    prompt:
                      "This peanut butter treat cupped in a ridged brown wrapper comes in packs of two and is made by Reese's.",
                    response: 'What are peanut butter cups?',
                    value: 200,
                  },
                  {
                    id: 'j-5-1',
                    prompt:
                      'These small pieces of chewy taffy come in flavors like banana and strawberry, each twisted in a black-and-orange wrapper.',
                    response: 'What are Mary Janes (or Kraft caramels / taffy)?',
                    value: 400,
                  },
                  {
                    id: 'j-5-2',
                    prompt:
                      'Ranked among the least popular by many trick-or-treaters, this triangular tri-colored waxy confection is shaped like a beehive.',
                    response: 'What is candy corn?',
                    value: 600,
                  },
                  {
                    id: 'j-5-3',
                    prompt:
                      'This crunchy, malted-milk-center sphere coated in chocolate shares its name with a term for a fielding error in baseball.',
                    response: 'What are Whoppers?',
                    value: 800,
                  },
                  {
                    id: 'j-5-4',
                    prompt:
                      'Created by Leo Hirschfield in 1907 and named for his daughter, this chewy chocolate log is often called the first penny candy in the U.S.',
                    response: 'What is a Tootsie Roll?',
                    value: 1000,
                  },
                ],
              },
            ],
          },
          {
            round: 'DOUBLE_JEOPARDY',
            categories: [
              {
                name: 'Halloween Costumes',
                clues: [
                  {
                    id: 'dj-0-0',
                    prompt:
                      'Wear a pointy black hat and carry a broomstick to dress as this spell-casting figure often seen stirring a bubbling cauldron.',
                    response: 'What is a witch?',
                    value: 400,
                  },
                  {
                    id: 'dj-0-1',
                    prompt:
                      'Paint your face white, add a red nose, floppy shoes, and a rainbow wig to dress as this circus performer — extra unsettling in horror films.',
                    response: 'What is a clown?',
                    value: 800,
                  },
                  {
                    id: 'dj-0-2',
                    prompt:
                      "A white sheet with two eye holes cut out is the classic quick costume for this floating spirit that says 'Boo!'",
                    response: 'What is a ghost?',
                    value: 1200,
                  },
                  {
                    id: 'dj-0-3',
                    prompt:
                      'Throw on a cape, a mask, and a chest emblem to dress up as one of these comic-book crime-fighters — a popular, less-scary Halloween pick.',
                    response: 'What is a superhero?',
                    value: 1600,
                  },
                  {
                    id: 'dj-0-4',
                    prompt:
                      'Don a long robe, a pointed hat, and a fake beard, and carry a staff to become this magic-wielding figure like Gandalf or Merlin.',
                    response: 'What is a wizard?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Haunted Places',
                clues: [
                  {
                    id: 'dj-1-0',
                    prompt:
                      'This crumbling old house at the top of the block, with broken windows and creaky floors, is the classic setting for a spooky sleepover dare.',
                    response: 'What is a haunted house?',
                    value: 400,
                  },
                  {
                    id: 'dj-1-1',
                    prompt:
                      "England's oldest of these fortified royal residences, at Windsor, is said to be roamed by the ghost of Henry VIII, whose limping footsteps echo in its halls.",
                    response: 'What is a castle?',
                    value: 800,
                  },
                  {
                    id: 'dj-1-2',
                    prompt:
                      'Ghost hunters flock to these final resting places at midnight, hoping to spot orbs or shadowy figures drifting among the tombstones.',
                    response: 'What is a cemetery (or graveyard)?',
                    value: 1200,
                    isDailyDouble: true,
                  },
                  {
                    id: 'dj-1-3',
                    prompt:
                      'Sailors have long feared this type of vessel, like the legendary Flying Dutchman, doomed to sail the seas forever with a phantom crew.',
                    response: 'What is a ship?',
                    value: 1600,
                  },
                  {
                    id: 'dj-1-4',
                    prompt:
                      "Many old ones of these medical buildings, such as Kentucky's Waverly Hills, closed after treating tuberculosis patients and are now famous for paranormal tours.",
                    response: 'What is a hospital (or sanatorium)?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Classic Monsters',
                clues: [
                  {
                    id: 'dj-2-0',
                    prompt:
                      'The title character of an H.G. Wells novel and a 1933 Universal film, this scientist wraps his head in bandages and dons goggles because his body cannot be seen.',
                    response: 'What is the Invisible Man?',
                    value: 400,
                  },
                  {
                    id: 'dj-2-1',
                    prompt:
                      "In Robert Louis Stevenson's tale, a respectable doctor drinks a potion that turns him into this cruel, monstrous alter ego.",
                    response: 'What is Mr. Hyde?',
                    value: 800,
                  },
                  {
                    id: 'dj-2-2',
                    prompt:
                      'Wrapped head to toe in ancient linen bandages, this preserved figure rises from its Egyptian tomb to stalk grave robbers.',
                    response: 'What is a mummy?',
                    value: 1200,
                  },
                  {
                    id: 'dj-2-3',
                    prompt:
                      'Stitched together from body parts and jolted to life by lightning, this bolt-necked being was created by a scientist who shares its common nickname.',
                    response: "What is Frankenstein's monster?",
                    value: 1600,
                  },
                  {
                    id: 'dj-2-4',
                    prompt:
                      'Living in the sewers beneath Paris and hiding his disfigured face behind a mask, this musical menace haunts the opera house above.',
                    response: 'What is the Phantom of the Opera?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Vampires',
                clues: [
                  {
                    id: 'dj-3-0',
                    prompt:
                      'This Irish author gave the world Count Dracula in his 1897 Gothic novel.',
                    response: 'Who is Bram Stoker?',
                    value: 400,
                  },
                  {
                    id: 'dj-3-1',
                    prompt:
                      'According to folklore, driving a wooden one of these through the heart was a way to destroy the undead.',
                    response: 'What is a stake?',
                    value: 800,
                  },
                  {
                    id: 'dj-3-2',
                    prompt:
                      'These blood-sucking flying mammals, often linked to the undead in legend, use echolocation to navigate the night.',
                    response: 'What are bats?',
                    value: 1200,
                  },
                  {
                    id: 'dj-3-3',
                    prompt:
                      "Anne Rice's 1976 novel about a reluctant immortal named Louis carried this title, later a Tom Cruise film.",
                    response: "What is 'Interview with the Vampire'?",
                    value: 1600,
                  },
                  {
                    id: 'dj-3-4',
                    prompt:
                      "This Romanian region, whose name means 'land beyond the forest,' is the fictional home of Stoker's famous count.",
                    response: 'What is Transylvania?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Halloween Traditions',
                clues: [
                  {
                    id: 'dj-4-0',
                    prompt:
                      'In this activity, kids in disguise go door to door on October 31st, offering a rhyming demand in exchange for candy.',
                    response: 'What is trick-or-treating?',
                    value: 400,
                  },
                  {
                    id: 'dj-4-1',
                    prompt:
                      'Kids and adults alike put on these outfits, dressing up as ghosts, superheroes, or their favorite characters for the spooky holiday.',
                    response: 'What are costumes?',
                    value: 800,
                  },
                  {
                    id: 'dj-4-2',
                    prompt:
                      'In this classic party game, players try to grab floating red fruit from a tub of water using only their mouths.',
                    response: 'What is bobbing for apples?',
                    value: 1200,
                  },
                  {
                    id: 'dj-4-3',
                    prompt:
                      'This medieval British custom of going door to door in disguise, offering a song or prayer in exchange for food, is a direct ancestor of trick-or-treating.',
                    response: 'What is souling (or guising)?',
                    value: 1600,
                    isDailyDouble: true,
                  },
                  {
                    id: 'dj-4-4',
                    prompt:
                      'In many neighborhoods, homes are decorated with fake spiderwebs and this style of gravestone marker, often bearing joking epitaphs.',
                    response: 'What are tombstones?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Spooky Songs',
                clues: [
                  {
                    id: 'dj-5-0',
                    prompt:
                      "This 1962 novelty hit by Bobby 'Boris' Pickett describes a dance craze that took place in a laboratory late one night.",
                    response: "What is 'Monster Mash'?",
                    value: 400,
                  },
                  {
                    id: 'dj-5-1',
                    prompt:
                      "Michael Jackson's 1983 hit of this name features a spoken-word ending by horror legend Vincent Price and a groundbreaking 14-minute music video.",
                    response: "What is 'Thriller'?",
                    value: 800,
                  },
                  {
                    id: 'dj-5-2',
                    prompt:
                      "Ray Parker Jr. asks 'Who ya gonna call?' in this 1984 theme song for a movie about paranormal exterminators.",
                    response: "What is 'Ghostbusters'?",
                    value: 1200,
                  },
                  {
                    id: 'dj-5-3',
                    prompt:
                      "In this song from 'The Nightmare Before Christmas,' the residents of a scary town introduce themselves and their frightful ways.",
                    response: "What is 'This Is Halloween'?",
                    value: 1600,
                  },
                  {
                    id: 'dj-5-4',
                    prompt:
                      "Written by Screamin' Jay Hawkins, this 1956 song later became a hit for Creedence Clearwater Revival and casts an eerie enchantment on a lover.",
                    response: "What is 'I Put a Spell on You'?",
                    value: 2000,
                  },
                ],
              },
            ],
          },
        ],
        finalJeopardy: {
          categoryName: 'Halloween Traditions',
          prompt:
            'This ancient Celtic festival marking the end of the harvest season, when people believed the boundary between the living and the dead grew thin, is considered a major origin of modern Halloween.',
          response: 'What is Samhain?',
        },
        defaultTimerDuration: 20,
        maxPlayers: 8,
      },
    },
    {
      boardId: 'fall',
      theme: 'Fall',
      description:
        'A themed autumn trivia board — leaves, apples, pumpkins, harvest, football, and Thanksgiving. Play the full board with friends.',
      audience: 'Family',
      published: true,
      pack: {
        name: 'Fall — Peril',
        version: '1.0.1',
        rounds: [
          {
            round: 'JEOPARDY',
            categories: [
              {
                name: 'Autumn Leaves',
                clues: [
                  {
                    id: 'j-0-0',
                    prompt:
                      'This orange pigment, also abundant in carrots, becomes visible in leaves once green chlorophyll fades in fall.',
                    response: 'What is carotene?',
                    value: 200,
                  },
                  {
                    id: 'j-0-1',
                    prompt:
                      'This two-word term for the vivid seasonal display of red, orange, and yellow leaves draws "leaf peepers" to tour New England each autumn.',
                    response: 'What is fall foliage?',
                    value: 400,
                  },
                  {
                    id: 'j-0-2',
                    prompt:
                      'This astronomical event around September 22nd, when day and night are nearly equal, marks the official start of fall in the Northern Hemisphere.',
                    response: 'What is the autumnal equinox?',
                    value: 600,
                  },
                  {
                    id: 'j-0-3',
                    prompt:
                      'The colorful red and purple hues in some fall foliage come from this class of pigments, which also color blueberries and red cabbage.',
                    response: 'What are anthocyanins?',
                    value: 800,
                  },
                  {
                    id: 'j-0-4',
                    prompt:
                      "This special layer of cells forms at the base of a leaf's stem, cutting off nutrients and causing the leaf to drop.",
                    response: 'What is the abscission layer?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Pumpkin Patch',
                clues: [
                  {
                    id: 'j-1-0',
                    prompt:
                      'On the night of October 31st, these fruits are hollowed out and carved with scary faces, then lit from within with a candle.',
                    response: "What are jack-o'-lanterns?",
                    value: 200,
                  },
                  {
                    id: 'j-1-1',
                    prompt:
                      'Pumpkins grow along a long, trailing stem of this type — the same kind that bears watermelons and cucumbers.',
                    response: 'What is a vine?',
                    value: 400,
                  },
                  {
                    id: 'j-1-2',
                    prompt:
                      "This spiced autumn beverage from a famous coffee chain, often abbreviated 'PSL,' helped make the gourd a fall flavor sensation.",
                    response: 'What is the Pumpkin Spice Latte?',
                    value: 600,
                  },
                  {
                    id: 'j-1-3',
                    prompt:
                      'Belonging to the genus Cucurbita, these gourds are technically classified in this botanical category since they develop from a flower and contain seeds.',
                    response: 'What is a fruit?',
                    value: 800,
                  },
                  {
                    id: 'j-1-4',
                    prompt:
                      'Competitive growers cultivate the Atlantic Giant variety, with record specimens topping this weight, exceeding a full ton and setting world records.',
                    response: 'What is 2,000 pounds (a ton)?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Animals in Autumn',
                clues: [
                  {
                    id: 'j-2-0',
                    prompt:
                      'This orange-and-black insect makes an epic multi-generational journey from Canada to central Mexico each fall to escape the cold.',
                    response: 'What is the monarch butterfly?',
                    value: 200,
                  },
                  {
                    id: 'j-2-1',
                    prompt:
                      'These marine mammals travel up to 10,000 miles round-trip between polar feeding grounds and tropical breeding lagoons, one of the longest journeys of any mammal.',
                    response: 'What is the humpback whale?',
                    value: 400,
                  },
                  {
                    id: 'j-2-2',
                    prompt:
                      'This sooty-plumed seabird holds the record for the longest annual migration, flying pole to pole to enjoy two summers each year.',
                    response: 'What is the Arctic tern?',
                    value: 600,
                  },
                  {
                    id: 'j-2-3',
                    prompt:
                      'This bushy-tailed backyard rodent spends autumn frantically burying acorns and nuts to cache food for the coming winter.',
                    response: 'What is a squirrel?',
                    value: 800,
                  },
                  {
                    id: 'j-2-4',
                    prompt:
                      "This physiological cue, tied to shortening daylight in autumn, triggers many species to fatten up and begin their seasonal journeys; it comes from the Greek for 'length of light.'",
                    response: 'What is photoperiod?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Thanksgiving',
                clues: [
                  {
                    id: 'j-3-0',
                    prompt:
                      'Pureed and spiced, then baked in a flaky crust, this orange-hued dessert is the classic sweet ending to the November feast.',
                    response: 'What is pumpkin pie?',
                    value: 200,
                  },
                  {
                    id: 'j-3-1',
                    prompt:
                      'This sweet, tart red sauce or jellied side dish is made from small bog-grown berries and often served in a can-shaped mold.',
                    response: 'What is cranberry sauce?',
                    value: 400,
                  },
                  {
                    id: 'j-3-2',
                    prompt:
                      'In 1621, these Native Americans of the Wampanoag confederacy joined the Plymouth colonists for the harvest feast now considered the first of this holiday.',
                    response: 'Who are the Wampanoag?',
                    value: 600,
                  },
                  {
                    id: 'j-3-3',
                    prompt:
                      'Benjamin Franklin reportedly admired this feast centerpiece bird, calling it a more respectable national symbol than the bald eagle.',
                    response: 'What is the turkey?',
                    value: 800,
                  },
                  {
                    id: 'j-3-4',
                    prompt:
                      'The day after the November feast is nicknamed for this color, marking the start of the holiday shopping season when retailers hope to turn a profit.',
                    response: 'What is Black Friday?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Harvest Festivals',
                clues: [
                  {
                    id: 'j-4-0',
                    prompt:
                      "This German festival that begins in late September in Munich celebrates the season with beer tents, pretzels, and lederhosen; its name literally means 'October meadow.'",
                    response: 'What is Oktoberfest?',
                    value: 200,
                  },
                  {
                    id: 'j-4-1',
                    prompt:
                      'Celebrated on the 15th day of the 8th lunar month, this Chinese festival features round pastries filled with lotus seed paste, eaten while gazing at the fullest moon of autumn.',
                    response: 'What is the Mid-Autumn Festival?',
                    value: 400,
                  },
                  {
                    id: 'j-4-2',
                    prompt:
                      "This major Jewish festival, whose name means 'booths,' has families build and dine in temporary huts to recall the harvest and the wandering in the wilderness.",
                    response: 'What is Sukkot?',
                    value: 600,
                  },
                  {
                    id: 'j-4-3',
                    prompt:
                      'This Indian festival of lights, celebrated after the autumn crops, honors the goddess Lakshmi with rows of oil lamps and fireworks over five days.',
                    response: 'What is Diwali?',
                    value: 800,
                  },
                  {
                    id: 'j-4-4',
                    prompt:
                      'Marking the end of the harvest season for the Celts, this ancient Gaelic festival held around November 1st is considered a forerunner of modern Halloween.',
                    response: 'What is Samhain?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Autumn Traditions',
                clues: [
                  {
                    id: 'j-5-0',
                    prompt:
                      "This activity involves visiting a farm to select the perfect orange gourd, which you might later turn into a jack-o'-lantern.",
                    response: 'What is pumpkin picking?',
                    value: 200,
                  },
                  {
                    id: 'j-5-1',
                    prompt:
                      'On the fourth Thursday of November, American families gather to feast on turkey and express gratitude on this holiday.',
                    response: 'What is Thanksgiving?',
                    value: 400,
                  },
                  {
                    id: 'j-5-2',
                    prompt:
                      'Kids in costumes go door to door demanding candy with this three-word phrase on the night of October 31st.',
                    response: "What is 'trick or treat'?",
                    value: 600,
                  },
                  {
                    id: 'j-5-3',
                    prompt:
                      'On cool fall nights, families gather at a fire pit to roast marshmallows and assemble this layered graham-cracker-and-chocolate campfire treat.',
                    response: "What are s'mores?",
                    value: 800,
                  },
                  {
                    id: 'j-5-4',
                    prompt:
                      'Popular at fall festivals, this maze is carved out of tall stalks of a grain also used to make tortillas and popcorn.',
                    response: 'What is a corn maze?',
                    value: 1000,
                  },
                ],
              },
            ],
          },
          {
            round: 'DOUBLE_JEOPARDY',
            categories: [
              {
                name: 'Apple Harvest',
                clues: [
                  {
                    id: 'dj-0-0',
                    prompt:
                      'This deep, wide container you sling over your shoulder or set on the ground is used to collect the fruit you pick from the trees.',
                    response: 'What is a basket (or bushel)?',
                    value: 400,
                  },
                  {
                    id: 'dj-0-1',
                    prompt:
                      'Once picked, the fruit is often kept in this cool, dark storage room to keep it fresh long into the winter months.',
                    response: 'What is a cellar?',
                    value: 800,
                  },
                  {
                    id: 'dj-0-2',
                    prompt:
                      'This baked dessert with a flaky crust and a cinnamon-spiced fruit filling is a classic autumn treat, famously described as very American.',
                    response: 'What is pie?',
                    value: 1200,
                  },
                  {
                    id: 'dj-0-3',
                    prompt:
                      'This spreadable, dark brown preserve is made by slow-cooking the fruit for hours with spices until it becomes thick, despite its dairy-sounding name.',
                    response: 'What is apple butter?',
                    value: 1600,
                  },
                  {
                    id: 'dj-0-4',
                    prompt:
                      'This device with a hand crank pierces, peels, and cores the fruit all at once, speeding up preparation during a big harvest.',
                    response: 'What is a peeler-corer (apple peeler)?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Fall Weather',
                clues: [
                  {
                    id: 'dj-1-0',
                    prompt:
                      'This colorless mass of tiny water droplets often blankets low-lying areas on crisp autumn mornings, reducing how far drivers can see.',
                    response: 'What is fog?',
                    value: 400,
                  },
                  {
                    id: 'dj-1-1',
                    prompt:
                      'On clear, chilly autumn nights, this thin coating of ice crystals forms on grass and car windshields when temperatures dip to 32°F.',
                    response: 'What is frost?',
                    value: 800,
                  },
                  {
                    id: 'dj-1-2',
                    prompt:
                      'This measurement of moisture in the air tends to drop in fall, making the season feel less muggy and more comfortable than summer.',
                    response: 'What is humidity?',
                    value: 1200,
                    isDailyDouble: true,
                  },
                  {
                    id: 'dj-1-3',
                    prompt:
                      'In autumn, this boundary where a mass of chilly air pushes out warmer air often triggers gusty winds, dropping temperatures, and rain or thunderstorms.',
                    response: 'What is a cold front?',
                    value: 1600,
                  },
                  {
                    id: 'dj-1-4',
                    prompt:
                      'Meteorologists use this term for the wobbling band of cold air circling the Arctic that can dip southward in fall, bringing sudden blasts of frigid weather.',
                    response: 'What is the polar vortex?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Football Season',
                clues: [
                  {
                    id: 'dj-2-0',
                    prompt:
                      "This scoring play is worth six points and happens when a player carries or catches the ball across the opponent's goal line.",
                    response: 'What is a touchdown?',
                    value: 400,
                  },
                  {
                    id: 'dj-2-1',
                    prompt:
                      'Kicked between the uprights, this play is worth three points.',
                    response: 'What is a field goal?',
                    value: 800,
                  },
                  {
                    id: 'dj-2-2',
                    prompt:
                      'The offense gets four of these plays to advance the ball ten yards and earn a fresh set.',
                    response: 'What is a down?',
                    value: 1200,
                  },
                  {
                    id: 'dj-2-3',
                    prompt:
                      'A quarterback commits this costly turnover when a defender catches a pass meant for the offense.',
                    response: 'What is an interception?',
                    value: 1600,
                  },
                  {
                    id: 'dj-2-4',
                    prompt:
                      'This NFL efficiency metric grades a quarterback on completions, yards, touchdowns, and interceptions on a scale that peaks at a famously odd 158.3.',
                    response: 'What is passer rating?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Fall Foods & Flavors',
                clues: [
                  {
                    id: 'dj-3-0',
                    prompt:
                      'Gathered by squirrels and roasted on city streets in fall, these hard-shelled tree seeds include the chestnut, walnut, and pecan.',
                    response: 'What are nuts?',
                    value: 400,
                  },
                  {
                    id: 'dj-3-1',
                    prompt:
                      'This warm brown spice from tree bark flavors apple pie and is sprinkled on lattes when the leaves turn.',
                    response: 'What is cinnamon?',
                    value: 800,
                  },
                  {
                    id: 'dj-3-2',
                    prompt:
                      'Pressed from autumn-harvested fruit and often served warm with a cinnamon stick, this cloudy amber drink is a fall favorite.',
                    response: 'What is apple cider?',
                    value: 1200,
                  },
                  {
                    id: 'dj-3-3',
                    prompt:
                      'This orange tuber, often mashed with brown sugar and marshmallows, graces many a Thanksgiving table.',
                    response: 'What is a sweet potato?',
                    value: 1600,
                  },
                  {
                    id: 'dj-3-4',
                    prompt:
                      'These reddish tart berries are boiled into a jiggly sauce that pairs with Thanksgiving turkey.',
                    response: 'What are cranberries?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Corn Mazes & Hayrides',
                clues: [
                  {
                    id: 'dj-4-0',
                    prompt:
                      'This flatbed farm vehicle, often pulled by a tractor and lined with straw bales for seating, gives its name to a popular fall attraction where riders bump along the fields.',
                    response: 'What is a wagon?',
                    value: 400,
                  },
                  {
                    id: 'dj-4-1',
                    prompt:
                      "Getting hopelessly turned around while wandering the winding paths of a fall attraction means you've done this 4-letter action, sharing a name with sheep who wander off.",
                    response: 'What is to get lost (be lost)?',
                    value: 800,
                  },
                  {
                    id: 'dj-4-2',
                    prompt:
                      'This dried, golden plant stalk material—not to be confused with the animal-bedding kind spelled differently—fills the wagon beds and provides the seating for autumn rides.',
                    response: 'What is hay?',
                    value: 1200,
                  },
                  {
                    id: 'dj-4-3',
                    prompt:
                      "Many autumn attractions add a spooky nighttime version, using this word borrowed from a friendly ghost's favorite adjective to describe a scary or 'spooky' walk through the paths.",
                    response: 'What is haunted?',
                    value: 1600,
                    isDailyDouble: true,
                  },
                  {
                    id: 'dj-4-4',
                    prompt:
                      'Designers often plot these puzzle attractions on this X-Y coordinate framework, a term for a network of evenly spaced perpendicular lines, before cutting the intricate patterns into the field.',
                    response: 'What is a grid?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Autumn Around the World',
                clues: [
                  {
                    id: 'dj-5-0',
                    prompt:
                      'This North American country celebrates its harvest holiday on the second Monday of October, a month before its southern neighbor does the same thing in November.',
                    response: 'What is Canada?',
                    value: 400,
                  },
                  {
                    id: 'dj-5-1',
                    prompt:
                      'In Germany, this early-autumn harvest thanksgiving, whose name means "harvest thanks festival," fills churches with a crown woven of wheat and grain.',
                    response: 'What is Erntedankfest?',
                    value: 800,
                  },
                  {
                    id: 'dj-5-2',
                    prompt:
                      "In Japan, crowds travel each fall to see this fiery display of red and gold maple leaves, a viewing tradition called 'momijigari.'",
                    response: 'What is autumn foliage (fall leaves)?',
                    value: 1200,
                  },
                  {
                    id: 'dj-5-3',
                    prompt:
                      'Koreans travel home for this major autumn harvest festival, honoring their ancestors and sharing half-moon rice cakes called songpyeon.',
                    response: 'What is Chuseok?',
                    value: 1600,
                  },
                  {
                    id: 'dj-5-4',
                    prompt:
                      'Celebrated in late October and early November in Mexico, this two-day observance honors deceased loved ones with marigolds, sugar skulls, and family altars called ofrendas.',
                    response: 'What is Día de los Muertos (Day of the Dead)?',
                    value: 2000,
                  },
                ],
              },
            ],
          },
        ],
        finalJeopardy: {
          categoryName: 'Thanksgiving',
          prompt:
            'This U.S. president issued a proclamation in 1863 establishing the last Thursday in November as a national day of thanks, largely thanks to the persistent letter-writing campaign of magazine editor Sarah Josepha Hale.',
          response: 'Who was Abraham Lincoln?',
        },
        defaultTimerDuration: 20,
        maxPlayers: 8,
      },
    },
    {
      boardId: 'oktoberfest',
      theme: 'Oktoberfest',
      description:
        "A themed Oktoberfest trivia board — Bavarian food and drink, German traditions, Munich, and the world's biggest fall festival. Play the full board with friends.",
      audience: 'Adults',
      published: true,
      pack: {
        name: 'Oktoberfest — Peril',
        version: '1.0.1',
        rounds: [
          {
            round: 'JEOPARDY',
            categories: [
              {
                name: 'Bavaria & Munich',
                clues: [
                  {
                    id: 'j-0-0',
                    prompt:
                      'This luxury automaker, whose initials stand for "Bavarian Motor Works," is headquartered in Munich and wears a blue-and-white roundel badge.',
                    response: 'What is BMW?',
                    value: 200,
                  },
                  {
                    id: 'j-0-1',
                    prompt:
                      "Located on Munich's central Marienplatz, this town hall square is famous for its animated glockenspiel that reenacts a knights' tournament.",
                    response: 'What is the New Town Hall (Neues Rathaus)?',
                    value: 400,
                  },
                  {
                    id: 'j-0-2',
                    prompt:
                      "This 'Mad King' of Bavaria commissioned the fairy-tale Neuschwanstein Castle before his mysterious 1886 death in Lake Starnberg.",
                    response: 'Who was Ludwig II?',
                    value: 600,
                  },
                  {
                    id: 'j-0-3',
                    prompt:
                      "Rising along Bavaria's southern border, this mountain range provides the region's dramatic alpine scenery and ski resorts.",
                    response: 'What are the Alps?',
                    value: 800,
                  },
                  {
                    id: 'j-0-4',
                    prompt:
                      'Flowing through Munich on its way to join the Danube, this river gives surfers a famous standing wave at the Englischer Garten.',
                    response: 'What is the Isar?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Beer & Brewing',
                clues: [
                  {
                    id: 'j-1-0',
                    prompt:
                      "This process converts sugars into alcohol and carbon dioxide, and it's carried out by yeast in every batch of beer.",
                    response: 'What is fermentation?',
                    value: 200,
                  },
                  {
                    id: 'j-1-1',
                    prompt:
                      'Before it can be added to the boil, this grain is soaked, germinated, and dried in a kiln to develop its fermentable sugars.',
                    response: 'What is malt?',
                    value: 400,
                  },
                  {
                    id: 'j-1-2',
                    prompt:
                      'These dried green cones from a climbing plant give beer its bitterness and aroma; varieties include Cascade and Saaz.',
                    response: 'What are hops?',
                    value: 600,
                  },
                  {
                    id: 'j-1-3',
                    prompt:
                      'This pale, hoppy style abbreviated with three letters grew from beer brewed strong enough to survive the long sea voyage to colonial South Asia.',
                    response: 'What is an IPA (India Pale Ale)?',
                    value: 800,
                  },
                  {
                    id: 'j-1-4',
                    prompt:
                      "This 1516 Bavarian law, whose name means 'purity order', originally limited the drink's ingredients to water, barley, and hops.",
                    response: 'What is the Reinheitsgebot?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Oktoberfest History',
                clues: [
                  {
                    id: 'j-2-0',
                    prompt:
                      "This European nation's southern state of Bavaria is home to the world's largest annual folk festival, first held in 1810.",
                    response: 'What is Germany?',
                    value: 200,
                  },
                  {
                    id: 'j-2-1',
                    prompt:
                      'To open the festivities, the mayor of Munich taps the first keg and shouts this celebratory two-word Bavarian phrase meaning "it is tapped."',
                    response: 'What is "O\'zapft is"?',
                    value: 400,
                  },
                  {
                    id: 'j-2-2',
                    prompt:
                      'The original 1810 event honored the union of Crown Prince Ludwig and his bride, an occasion celebrated with days of festivities and horse races.',
                    response: 'What is a wedding?',
                    value: 600,
                  },
                  {
                    id: 'j-2-3',
                    prompt:
                      "The large open field where the festivities are held is named after this bride of Ludwig, whose name gave the grounds their nickname 'the Wies'n'.",
                    response: 'Who is Therese?',
                    value: 800,
                  },
                  {
                    id: 'j-2-4',
                    prompt:
                      'Under strict local purity traditions, only brews meeting a certain strength and made within the host city may be served in the giant tents; each holds this many milliliters, the volume of the classic Maß glass.',
                    response: 'What is 1,000 (one liter)?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Pretzels & Snacks',
                clues: [
                  {
                    id: 'j-3-0',
                    prompt:
                      'This coarse-grained variety of a common seasoning is the crunchy white topping sprinkled on soft baked twists at the ballpark.',
                    response: 'What is salt?',
                    value: 200,
                  },
                  {
                    id: 'j-3-1',
                    prompt:
                      'This popular German street food tops sliced pork sausage with a spiced, curried ketchup sauce.',
                    response: 'What is currywurst?',
                    value: 400,
                  },
                  {
                    id: 'j-3-2',
                    prompt:
                      'This tangy yellow condiment, made from ground seeds of a plant in the Brassica family, is the classic dip for a warm doughy twist.',
                    response: 'What is mustard?',
                    value: 600,
                  },
                  {
                    id: 'j-3-3',
                    prompt:
                      "This snack company known for its 'Bold' and 'Butter Snap' varieties shares its name with a term for the small farms and businesses of Pennsylvania Dutch country.",
                    response: "What is Snyder's (of Hanover)?",
                    value: 800,
                  },
                  {
                    id: 'j-3-4',
                    prompt:
                      'This deep-fried corn tortilla chip, often served with cheese sauce and jalapenos, gets its name from a Mexican restaurateur named Ignacio.',
                    response: 'What are nachos?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Beer Mugs & Tents',
                clues: [
                  {
                    id: 'j-4-0',
                    prompt:
                      'This heavy German drinking vessel with a hinged lid was originally covered to keep out plague-carrying flies.',
                    response: 'What is a stein?',
                    value: 200,
                  },
                  {
                    id: 'j-4-1',
                    prompt:
                      'Campers pound these pointed spikes into the ground to secure the corners and guy-lines of their shelter.',
                    response: 'What are stakes (or pegs)?',
                    value: 400,
                  },
                  {
                    id: 'j-4-2',
                    prompt:
                      'Many traditional German mugs are made of this fired clay material, often glazed in gray and cobalt blue.',
                    response: 'What is stoneware (or ceramic)?',
                    value: 600,
                  },
                  {
                    id: 'j-4-3',
                    prompt:
                      'This large, sturdy shelter houses the main ring and audiences at a traveling circus.',
                    response: 'What is the big top?',
                    value: 800,
                  },
                  {
                    id: 'j-4-4',
                    prompt:
                      'Lidded mugs are often decorated with the antler-topped horn of this hunted forest animal, a symbol of Bavarian tradition.',
                    response: 'What is a stag (deer)?',
                    value: 1000,
                  },
                ],
              },
              {
                name: 'Alpine Culture',
                clues: [
                  {
                    id: 'j-5-0',
                    prompt:
                      'This long wooden horn, wider at the base, was historically used by herders to communicate across mountain valleys.',
                    response: 'What is the alphorn?',
                    value: 200,
                  },
                  {
                    id: 'j-5-1',
                    prompt:
                      'This style of singing involves rapid pitch changes between low chest voice and high falsetto, echoing across peaks.',
                    response: 'What is yodeling?',
                    value: 400,
                  },
                  {
                    id: 'j-5-2',
                    prompt:
                      'Cattle are led up to high mountain pastures in summer and back down in autumn during this seasonal migration.',
                    response: 'What is transhumance?',
                    value: 600,
                  },
                  {
                    id: 'j-5-3',
                    prompt:
                      'This hearty melted cheese dish, scraped from a heated half-wheel onto potatoes, shares its name with the cheese itself.',
                    response: 'What is raclette?',
                    value: 800,
                  },
                  {
                    id: 'j-5-4',
                    prompt:
                      'Traditional short leather trousers with suspenders, worn in Bavaria and Austria, go by this German name.',
                    response: 'What are lederhosen?',
                    value: 1000,
                  },
                ],
              },
            ],
          },
          {
            round: 'DOUBLE_JEOPARDY',
            categories: [
              {
                name: 'German Food',
                clues: [
                  {
                    id: 'dj-0-0',
                    prompt:
                      'This pale, spiced pork product served in pairs is a Munich breakfast staple, traditionally eaten before noon with sweet mustard.',
                    response: 'What is Weisswurst?',
                    value: 400,
                  },
                  {
                    id: 'dj-0-1',
                    prompt:
                      "This cabbage dish is fermented in brine, giving it a tangy flavor and a name that literally translates to 'sour cabbage.'",
                    response: 'What is sauerkraut?',
                    value: 800,
                  },
                  {
                    id: 'dj-0-2',
                    prompt:
                      'These soft egg noodles, often served with cheese or as a side to meat dishes, hail from the Swabian region.',
                    response: 'What is Spätzle?',
                    value: 1200,
                  },
                  {
                    id: 'dj-0-3',
                    prompt:
                      'This Black Forest specialty layers chocolate cake with cherries and whipped cream, spiked with a cherry brandy.',
                    response: 'What is Schwarzwälder Kirschtorte (Black Forest cake)?',
                    value: 1600,
                  },
                  {
                    id: 'dj-0-4',
                    prompt:
                      "Meaning 'meat cheese' though it contains neither exclusively, this baked loaf of finely ground pork and beef is sliced and often served fried in a bun.",
                    response: 'What is Leberkäse?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Lederhosen & Dirndl',
                clues: [
                  {
                    id: 'dj-1-0',
                    prompt:
                      'On a dirndl, the side on which this decorative waist accessory is tied is said to signal whether a woman is single or taken; on the left it traditionally means available.',
                    response: 'What is the apron (or bow)?',
                    value: 400,
                  },
                  {
                    id: 'dj-1-1',
                    prompt:
                      'This southeastern German state, home to Munich and the Alps, is the region most associated with these traditional folk garments.',
                    response: 'What is Bavaria?',
                    value: 800,
                  },
                  {
                    id: 'dj-1-2',
                    prompt:
                      'The dirndl bodice is often laced up the front with a cord threaded through pairs of these small metal rings or holes.',
                    response: 'What are eyelets (or grommets)?',
                    value: 1200,
                    isDailyDouble: true,
                  },
                  {
                    id: 'dj-1-3',
                    prompt:
                      'Worn with lederhosen, these knitted calf-warmers, often called Loferl when footless, leave the ankle bare above chunky shoes.',
                    response: 'What are (Bavarian) socks or stockings?',
                    value: 1600,
                  },
                  {
                    id: 'dj-1-4',
                    prompt:
                      "This German compound word for the overall style of traditional regional dress translates literally as 'costume' or 'folk garb' and covers both the men's and women's outfits.",
                    response: 'What is Tracht?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'German Traditions',
                clues: [
                  {
                    id: 'dj-2-0',
                    prompt:
                      'Each spring, Bavarian villages raise a tall pole painted in white-and-blue spirals and topped with a wreath, then guard it against theft by rival towns.',
                    response: 'What is a maypole (Maibaum)?',
                    value: 400,
                  },
                  {
                    id: 'dj-2-1',
                    prompt:
                      'On the night of December 5th, children set out a boot hoping this figure will fill it with treats rather than leaving switches for the naughty.',
                    response: 'Who is St. Nicholas (Nikolaus)?',
                    value: 800,
                  },
                  {
                    id: 'dj-2-2',
                    prompt:
                      'Come Advent season, town squares fill with wooden stalls selling mulled Glühwein, roasted almonds, and handmade ornaments at one of these seasonal markets.',
                    response: 'What is a Christmas market (Weihnachtsmarkt)?',
                    value: 1200,
                  },
                  {
                    id: 'dj-2-3',
                    prompt:
                      "During the pre-Lenten 'fifth season' known as Karneval or Fasching, cities like Cologne erupt in parades, costumes, and satirical floats before Ash Wednesday.",
                    response: 'What is Carnival?',
                    value: 1600,
                  },
                  {
                    id: 'dj-2-4',
                    prompt:
                      "Rooted in a 19th-century student gymnastics movement, these local athletic clubs, whose name comes from the German for 'gymnastics,' foster community sport across the country.",
                    response: 'What are Turnverein (gymnastics clubs)?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Bavarian Music & Dance',
                clues: [
                  {
                    id: 'dj-3-0',
                    prompt:
                      'This large wind instrument you blow while resting one end on the ground gives its low, booming sound to many oom-pah bands in beer halls.',
                    response: 'What is the tuba?',
                    value: 400,
                  },
                  {
                    id: 'dj-3-1',
                    prompt:
                      "Wrapped in a horn near the top of a brass band, this valved instrument plays the perky 'off-beat' oom-pahs and lends its name to a whole class of German folk groups.",
                    response: 'What is the trumpet (Trompete)?',
                    value: 800,
                  },
                  {
                    id: 'dj-3-2',
                    prompt:
                      'A staple of Alpine bands, this box-shaped, bellows-driven, button-and-key instrument is squeezed and pulled to produce polka melodies.',
                    response: 'What is the accordion?',
                    value: 1200,
                  },
                  {
                    id: 'dj-3-3',
                    prompt:
                      "This lively couple's dance in triple meter, born in central Europe and popular in ballrooms, features spinning partners gliding across the floor.",
                    response: 'What is the waltz?',
                    value: 1600,
                  },
                  {
                    id: 'dj-3-4',
                    prompt:
                      'This yodel-friendly vocal or instrumental piece, sometimes performed atop a long wooden horn, celebrates herders and mountain pastures.',
                    response: 'What is an Alphorn call (Kuhreihen / ranz des vaches)?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'German Language',
                clues: [
                  {
                    id: 'dj-4-0',
                    prompt:
                      "This most common word in German means 'the,' but it comes in masculine, feminine, and neuter forms depending on the noun's gender.",
                    response: "What is 'der' (or der/die/das)?",
                    value: 400,
                  },
                  {
                    id: 'dj-4-1',
                    prompt:
                      "In German writing, all of these are capitalized, not just proper ones — so 'the house' becomes 'das Haus.'",
                    response: 'What are nouns?',
                    value: 800,
                  },
                  {
                    id: 'dj-4-2',
                    prompt:
                      "Modified vowels like ä, ö, and ü carry this German name that literally means 'changed sound.'",
                    response: 'What are umlauts?',
                    value: 1200,
                  },
                  {
                    id: 'dj-4-3',
                    prompt:
                      "This distinctive double-s ligature, written ß, is used after long vowels, as in the word for 'street.'",
                    response: 'What is the eszett (or scharfes S)?',
                    value: 1600,
                    isDailyDouble: true,
                  },
                  {
                    id: 'dj-4-4',
                    prompt:
                      "German is famous for these lengthy words, formed by joining smaller words together, like 'Handschuh' for glove ('hand-shoe').",
                    response: 'What are compound words?',
                    value: 2000,
                  },
                ],
              },
              {
                name: 'Fall Festivals Worldwide',
                clues: [
                  {
                    id: 'dj-5-0',
                    prompt:
                      'This Indian festival of lights celebrates the triumph of good over evil with oil lamps, fireworks, and sweets, typically in October or November.',
                    response: 'What is Diwali?',
                    value: 400,
                  },
                  {
                    id: 'dj-5-1',
                    prompt:
                      "Rooted in Celtic Samhain and observed October 31st, this holiday features costumes, jack-o'-lanterns, and trick-or-treating.",
                    response: 'What is Halloween?',
                    value: 800,
                  },
                  {
                    id: 'dj-5-2',
                    prompt:
                      'This November 1st and 2nd Mexican tradition honors deceased loved ones with sugar skulls, marigolds, and ofrendas.',
                    response: 'What is Dia de los Muertos (Day of the Dead)?',
                    value: 1200,
                  },
                  {
                    id: 'dj-5-3',
                    prompt:
                      'This Chinese autumn celebration centers on family reunions, lanterns, and eating round pastries filled with lotus paste under a full moon.',
                    response: 'What is the Mid-Autumn Festival (Moon Festival)?',
                    value: 1600,
                  },
                  {
                    id: 'dj-5-4',
                    prompt:
                      'Held over nine nights in autumn, this Hindu celebration honors the goddess Durga with dancing forms like garba and dandiya-raas.',
                    response: 'What is Navaratri?',
                    value: 2000,
                  },
                ],
              },
            ],
          },
        ],
        finalJeopardy: {
          categoryName: 'Oktoberfest',
          prompt:
            "This Bavarian capital hosts the world's largest folk festival each autumn, drawing over 6 million visitors annually to its fairgrounds since the celebration began in 1810 to honor a royal wedding.",
          response: 'What is Munich?',
        },
        defaultTimerDuration: 20,
        maxPlayers: 8,
      },
    },
  ],
};

/** Convenience: the boards list (what consumers iterate). */
export const TRIVIA_BOARDS = TRIVIA_BOARD_SET.boards;
